import { createContext, useCallback, useState, useContext, FC } from 'react';

import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { User, UserDataStateLocation } from '~/models';
import api from '~/services/api';

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentialsProfessional {
  cpf: string;
  password: string;
}

interface AuthContextData {
  loading: boolean;
  user: User;
  userData: UserDataStateLocation;
  storageUserData(userInfo: UserDataStateLocation): void;
  toggleToRememberMe(): void;
  signOut(): void;
  updateUser(user: User): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  signInProfessional(credentials: SignInCredentialsProfessional): any;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: FC = ({ children }) => {
  const { push } = useHistory();
  const [data, setData] = useState<AuthState>(() => {
    const existUserInLocalStorage = localStorage.getItem('IB:token');

    let token = null;
    let user = null;

    if (existUserInLocalStorage) {
      token = localStorage.getItem('IB:token');
      user = localStorage.getItem('IB:user');
    } else {
      token = sessionStorage.getItem('IB:token');
      user = sessionStorage.getItem('IB:user');
    }

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [userData, setUserData] = useState({} as UserDataStateLocation);

  const toggleToRememberMe = useCallback(() => {
    setRememberMe((prevState) => !prevState);
    console.log('checkboxRememberMe', rememberMe);

    localStorage.setItem('IB:rememberMe', JSON.stringify(!rememberMe));
  }, [rememberMe]);

  const signInProfessional = useCallback(
    // eslint-disable-next-line consistent-return
    async ({ cpf, password }: SignInCredentialsProfessional) => {
      try {
        setLoading((prevState) => !prevState);

        const response = await api.post('/auth/signin', {
          cpf,
          password,
        });

        const { user } = response.data;
        const { token } = response.data.data;

        const remember = localStorage.getItem('IB:rememberMe');

        if (remember) {
          localStorage.setItem('IB:token', token);
          localStorage.setItem('IB:user', JSON.stringify(user));
        } else if (!remember || remember === null) {
          sessionStorage.setItem('IB:token', token);
          sessionStorage.setItem('IB:user', JSON.stringify(user));
        }

        api.defaults.headers.authorization = `Bearer ${token}`;

        toast.success('Autenticação realizada com sucesso!');

        setData({ token, user });

        return response;
      } catch (error) {
        toast.dark('Falha na autenticação, verifique seus dados');
      } finally {
        setLoading((prevState) => !prevState);
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    sessionStorage.removeItem('IB:token');
    sessionStorage.removeItem('IB:user');

    localStorage.removeItem('IB:token');
    localStorage.removeItem('IB:user');
    localStorage.removeItem('IB:rememberMe');

    push('/');

    setData({} as AuthState);
    setLoading(false);
  }, [push]);

  const updateUser = useCallback(
    (user: User) => {
      const remember = localStorage.getItem('IB:rememberMe');

      if (remember) {
        localStorage.setItem('IB:user', JSON.stringify(user));
      } else if (!remember || remember === null) {
        sessionStorage.setItem('IB:user', JSON.stringify(user));
      }

      setData({
        token: data.token,
        user,
      });
    },
    [setData, data.token],
  );

  const storageUserData = useCallback((userInfo: UserDataStateLocation) => {
    setUserData(userInfo);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        loading,
        storageUserData,
        userData,
        user: data.user,
        toggleToRememberMe,
        signOut,
        updateUser,
        signInProfessional,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}

export { AuthProvider, useAuth };
